// src/services/api.js
import axios from 'axios';
import { auth } from '../config/firebase';
import { Buffer } from 'buffer';

const BASE_URL = 'https://www.ewakili.com/api'; // Replace with your actual API base URL

export const createApi = (token) => {
  const api = axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    },
    timeout: 30000, // Set a timeout of 30 seconds
  });

  // Add a response interceptor for error handling
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const customError = new Error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'An unknown error occurred'
      );
      customError.status = error.response?.status;
      customError.code = error.response?.data?.code;
      return Promise.reject(customError);
    }
  );

  // Add request interceptor for retries and token updates
  api.interceptors.request.use(async (config) => {
    let currentToken = token;
    if (!currentToken) {
      console.warn("No token found when creating a request!");
      // If no token, but user is authenticated, try to get a fresh token
      try {
        const user = auth.currentUser;
        if (user) {
          currentToken = await user.getIdToken(true);
          console.info("Token refreshed in request interceptor");
        }
      } catch (e) {
        console.error("Failed to refresh token:", e.message);
      }
    }

    let retryCount = 0;
    while (retryCount < 3) {
      // Retry up to 3 times
      try {
        config.headers.Authorization = currentToken ? `Bearer ${currentToken}` : '';
        return config;
      } catch (e) {
        retryCount++;
        console.error(`Failed to create request after ${retryCount} retries. ${e.message}`);
        if (retryCount >= 3) throw e;
        await new Promise((resolve) => setTimeout(resolve, retryCount * 2000));
      }
    }
    throw new Error('Failed to create request after multiple attempts');
  },
    (error) => {
      console.error('Error creating request', error);
      return Promise.reject(error);
    }
  );

  return {
    submitQuestion: async (questionData) => {
      try {
        const response = await api.post('/case-retriever/', {
          question: questionData.question,
          question_type: questionData.selectedType,
        });
        return response.data;
      } catch (error) {
        throw new Error(error.message || 'Failed to submit question');
      }
    },

    checkTaskStatus: async (taskId) => {
      try {
        const response = await api.get(`/task-status/${taskId}/`);
        return response.data;
      } catch (error) {
        throw new Error(error.message || 'Failed to check task status');
      }
    },

    getQuestionDetail: async (questionId) => {
      try {
        const response = await api.get(`/question-detail/${questionId}/`);
        return response.data;
      } catch (error) {
        throw new Error(error.message || 'Failed to get question details');
      }
    },

    getBalance: async () => {
      try {
        const response = await api.get('/get-balance/');
        return response.data;
      } catch (error) {
        throw new Error(error.message || 'Failed to fetch balance');
      }
    },

    get: async (url) => {
      try {
        const response = await api.get(url);
        return response.data;
      } catch (error) {
        throw new Error(error.message || 'Failed to fetch from url: ' + url);
      }
    },

    getTransactionHistory: async () => {
      try {
        const response = await api.get('/user_transaction_history/');
        return response.data;
      } catch (error) {
        throw new Error(error.message || 'Failed to fetch transaction history');
      }
    },

    // Generic POST method added to the API object
    post: async (url, data) => {
      try {
        const response = await api.post(url, data);
        return response.data;
      } catch (error) {
        console.error("Error in post request:", error);

        let errorMessage = 'Failed to post to url: ' + url;

        if (error.response) {
          errorMessage = error.response.data.message || error.response.data.error || errorMessage;
        } else if (error.request) {
          errorMessage = 'No response received from the server';
        }

        throw new Error(errorMessage);
      }
    },
  };
};